import * as React from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { DataGrid, type DataGridColumn } from '@/components/ui/data-grid';
import { EmptyState } from '@/components/ui/empty-state';
import type { PostureStatus } from '@/components/ui/posture-chip';
import { SectorChip } from './sector-chip';
import { TeamAvatars, type TeamMember } from './team-avatars';
import { formatDate } from '@/lib/utils/format';
import { cn } from '@/lib/utils/cn';

/**
 * ClientsTable — the firm-level table on `/clients` (T-0108 / C20-130).
 *
 * Mirrors the table in `design_handoff_caliber_v2/prototype/screens-gaps.js
 * → Screens.clients`. Columns:
 *
 *   1. Client            — name + initials tile (no id: C2-31)
 *   2. Sector            — <SectorChip> color-coded by client_type
 *   3. Primary contact   — name + email
 *   4. Active engagement — multi-line list of engagements with posture dot
 *   5. Engagement team   — <TeamAvatars> (max 4 + "+N more")
 *   6. Onboarded         — formatted timestamp from created_at
 *   7. Status            — chip
 *   8. Actions           — Edit button
 *
 * Row click → drills into `/clients/:id`. Built on the shared `<DataGrid>`
 * primitive so the table density + hover/focus treatments stay consistent
 * with the issues / audit / snapshot list grids.
 */

export interface ClientsTableEngagement {
  id: string;
  name: string;
  posture: PostureStatus;
  phase?: string;
}

export interface ClientsTableRow {
  id: string;
  name: string;
  client_type: string;
  status: string;
  primary_contact_name?: string;
  primary_contact_email?: string;
  engagements: ReadonlyArray<ClientsTableEngagement>;
  team: ReadonlyArray<TeamMember>;
  onboarded?: string;
  last_activity?: string;
}

export interface ClientsTableProps {
  rows: ReadonlyArray<ClientsTableRow>;
}

const POSTURE_DOT: Record<PostureStatus, string> = {
  healthy: 'bg-posture-healthy',
  'at-risk': 'bg-posture-at-risk',
  critical: 'bg-posture-critical',
  unassessed: 'bg-posture-unassessed',
};

export function ClientsTable({ rows }: ClientsTableProps) {
  const navigate = useNavigate();

  const columns = React.useMemo<ReadonlyArray<DataGridColumn<ClientsTableRow>>>(
    () => [
      {
        key: 'client',
        header: 'Client',
        cell: (row) => (
          <div className="flex items-center gap-3">
            <span
              aria-hidden="true"
              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-chip border border-ink-100 bg-paper-100 font-mono text-[11px] font-semibold text-ink-700"
            >
              {row.name
                .split(/\s+/)
                .filter(Boolean)
                .slice(0, 2)
                .map((w) => w.charAt(0))
                .join('')
                .toUpperCase()}
            </span>
            <div className="min-w-0">
              <div className="truncate text-body-sm font-medium text-ink-900">{row.name}</div>
              {/* C2-31: no id under the name; the Sector column already names
                  the client type, and the row has no other sub-label to show. */}
            </div>
          </div>
        ),
      },
      {
        key: 'sector',
        header: 'Sector',
        cell: (row) => <SectorChip sector={row.client_type} />,
        headerClassName: 'w-[130px]',
      },
      {
        key: 'contact',
        header: 'Primary contact',
        cell: (row) =>
          row.primary_contact_name ? (
            <div className="min-w-0">
              <div className="truncate text-body-sm font-medium text-ink-900">
                {row.primary_contact_name}
              </div>
              {row.primary_contact_email ? (
                <div className="truncate font-mono text-[11px] text-ink-500">
                  {row.primary_contact_email}
                </div>
              ) : null}
            </div>
          ) : (
            <span className="text-meta text-ink-300">—</span>
          ),
        headerClassName: 'w-[220px]',
      },
      {
        key: 'engagements',
        header: 'Active engagements',
        cell: (row) =>
          row.engagements.length === 0 ? (
            <span className="text-meta text-ink-500">No active project</span>
          ) : (
            <ul className="m-0 flex flex-col gap-0.5 p-0" style={{ listStyle: 'none' }}>
              {row.engagements.map((e) => (
                <li key={e.id} className="m-0 p-0">
                  <Link
                    to="/engagements/$engagementId"
                    params={{ engagementId: e.id }}
                    className="inline-flex items-center gap-1.5 text-meta font-medium text-ink-700 no-underline hover:text-ink-900"
                    onClick={(ev) => ev.stopPropagation()}
                  >
                    <span
                      aria-hidden="true"
                      className={cn(
                        'inline-block h-1.5 w-1.5 rounded-full',
                        POSTURE_DOT[e.posture],
                      )}
                    />
                    <span className="truncate">{e.name}</span>
                    {e.phase ? <span className="truncate text-ink-500">· {e.phase}</span> : null}
                  </Link>
                </li>
              ))}
            </ul>
          ),
        headerClassName: 'w-[260px]',
      },
      {
        key: 'team',
        header: 'Engagement team',
        cell: (row) => <TeamAvatars members={row.team} max={4} size={28} />,
        headerClassName: 'w-[180px]',
      },
      {
        key: 'onboarded',
        header: 'Onboarded',
        cell: (row) =>
          row.onboarded ? (
            <span className="font-mono text-[11px] text-ink-500">{formatDate(row.onboarded)}</span>
          ) : (
            <span className="text-meta text-ink-300">—</span>
          ),
        headerClassName: 'w-[120px]',
      },
      {
        key: 'status',
        header: 'Status',
        cell: (row) =>
          row.status.toLowerCase() === 'active' ? (
            <span className="inline-flex h-[22px] items-center rounded-full bg-posture-healthy-tint px-2.5 text-meta font-medium text-posture-healthy">
              Active
            </span>
          ) : (
            <span className="inline-flex h-[22px] items-center rounded-full bg-paper-100 px-2.5 text-meta font-medium text-ink-700">
              {row.status}
            </span>
          ),
        headerClassName: 'w-[100px]',
      },
    ],
    [],
  );

  return (
    <DataGrid<ClientsTableRow>
      columns={columns}
      rows={rows}
      rowKey={(row) => row.id}
      caption="Clients table"
      onRowClick={(row) => {
        void navigate({ to: '/clients/$clientId', params: { clientId: row.id } });
      }}
      emptyState={
        <EmptyState
          title="No clients yet"
          description="You don't have access to any clients. Ask a firm admin to add you, or seed local data via the API."
        />
      }
    />
  );
}

ClientsTable.displayName = 'ClientsTable';
